import { getApiUrl } from './config';
import type { ParcelPanelContext } from './parcel-panel-context-types';
import type { ParcelPanelResponse } from './parcel-panel-types';

async function readPanelJson(path: string): Promise<unknown> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const password = localStorage.getItem('pp-auth');
    const response = await fetch(`${getApiUrl()}${path}`, {
      headers: password ? { Authorization: `Bearer ${password}` } : {},
      cache: 'no-store',
      signal: controller.signal,
    });
    if (response.status === 401) throw new Error('Your session expired. Sign in again to load this parcel.');
    if (response.status === 404) throw new Error('This parcel is no longer available.');
    if (!response.ok) throw new Error('The saved data could not be loaded. Try again.');
    return await response.json();
  } catch (error) {
    if (controller.signal.aborted) throw new Error('The read reached its time limit. Try again.');
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

export function createParcelPanelClient(request: (path: string) => Promise<unknown> = readPanelJson) {
  const inFlight = new Map<string, Promise<unknown>>();
  function get<T>(path: string, validate: (value: unknown) => T): Promise<T> {
    const existing = inFlight.get(path);
    if (existing) return existing as Promise<T>;
    const pending = request(path).then(validate).finally(() => inFlight.delete(path));
    inFlight.set(path, pending);
    return pending;
  }
  return {
    snapshot(id: string): Promise<ParcelPanelResponse> {
      return get(`/parcel-panel/${encodeURIComponent(id)}`, value => {
        const data = value as ParcelPanelResponse;
        if (data?.subject?.parcel_id !== id || data?.parcel?.id !== id) {
          throw new Error('The response did not match the selected parcel. Try again.');
        }
        return data;
      });
    },
    context(id: string): Promise<ParcelPanelContext> {
      return get(`/parcel-panel/${encodeURIComponent(id)}/context`, value => {
        const data = value as ParcelPanelContext;
        if (data?.parcel_id !== id) throw new Error('The context did not match the selected parcel. Try again.');
        return data;
      });
    },
  };
}

export const parcelPanelClient = createParcelPanelClient();
